import { DEFAULT_SORT_ORDER } from '@/redux/modules/search/search.types';
import paramTypes from '@/types/search/enums/paramTypes';
import { SearchSortAndDirection } from '@/types/search/enums/sortTypes';
import { SearchQuery } from '@/types/search/searchQuery';
import { getBrowseDimensionKeyFromFacetType, parseBrowseCategoryPath } from '@/utils/browsePath/browsePath';
import {
    getCategoryNameFromPathname,
    getIsCollectionsPage,
    getIsSEOSearchPage,
    getIsSEOSearchPageWithLocation,
    isSimilarItemsPage,
    urlSafeTitle,
} from '@/utils/urls';

import FacetTypes from '../../types/search/enums/facetTypes';
import { getFacetFromSearchQueryParameter, getQueryParamValueDelimiter } from '../queryParams';
import { translateStatus } from './searchUtils';
// reject doesn't exist in es-toolkit, using native filter instead

interface BuildSearchQueryParams {
    catalogId?: number | undefined;
    defaultSortOrder?: SearchSortAndDirection;
    pathname?: string;
    // TODO type search params eventually / remove
    queryParams?: {
        [key: string]: any;
        sort?: SearchSortAndDirection;
    };
    /** Facet option ids from `/c/{slug}/dimension/value/…` browse URLs (merged into `options`). */
    browseFacetOptionIds?: Record<string, string[]>;
    searchPageSize?: number;
    similarItems?: boolean;
}

export type FlatFacetForCategoryResolution = {
    id: string;
    options?: { id?: string | number; name?: string }[];
};

/**
 * Same filter as in `buildSearchQuery` for resolving which flat facet owns a `category_id` segment.
 */
export const getFlatCategoryFacetsForCategoryIdResolution = (
    rawFacets: FlatFacetForCategoryResolution[]
): FlatFacetForCategoryResolution[] =>
    rawFacets.filter(
        (facet) =>
            !(
                facet.id === FacetTypes.CATEGORY_ID ||
                facet.id === FacetTypes.ORIGIN_ID ||
                !facet.options ||
                facet.id === FacetTypes.AUCTION_HOUSE_ID
            )
    );

/**
 * Flat facets (creator, style/period, etc.) all serialize into `category_id`. When the same option id appears in
 * more than one facet (aggregate collision), the first match in rawFacets order was used — often attributing a
 * style id to creator. Prefer a fixed facet order when multiple facets contain the id.
 */
/** Order used for `category_id` disambiguation and for hiding duplicate options on lower-precedence facets. */
export const FLAT_FACET_PRECEDENCE_FOR_SHARED_CATEGORY_ID: readonly string[] = [
    FacetTypes.STYLE_PERIOD_ID,
    FacetTypes.CREATOR_ID,
    FacetTypes.MATERIALS_AND_TECHNIQUES_ID,
    FacetTypes.ART_SUBJECT_ID,
];

const flatFacetOptionIdMatches = (categoryId: string, optionId: unknown): boolean =>
    String(optionId) === String(categoryId);

export const resolveFlatFacetForCategoryId = (
    flatCategoryFacets: FlatFacetForCategoryResolution[],
    categoryId: string
): FlatFacetForCategoryResolution | undefined => {
    const idNormalized = String(categoryId).trim();
    const matches = flatCategoryFacets.filter((facet) =>
        facet.options?.some((option) => flatFacetOptionIdMatches(idNormalized, option.id))
    );
    if (matches.length === 0) {
        return undefined;
    }
    if (matches.length === 1) {
        return matches[0];
    }
    for (const facetType of FLAT_FACET_PRECEDENCE_FOR_SHARED_CATEGORY_ID) {
        const found = matches.find((f) => f.id === facetType);
        if (found) {
            return found;
        }
    }
    return matches[0];
};

/**
 * When `category_id` lists an id that exists on multiple flat facets, the search API may still mark the wrong
 * facet's option as selected. If this option id appears in `category_id`, treat only the resolved facet as checked.
 */
export const isFlatFacetOptionCheckedForCategoryIdUrl = (
    queryParams: Record<string, unknown>,
    optionId: string | number,
    targetFacetId: string,
    rawFacets: FlatFacetForCategoryResolution[],
    selectedFromSearchFacet: boolean
): boolean => {
    const categoryIds = queryParams[paramTypes.CATEGORY_ID];
    if (categoryIds === undefined || categoryIds === null || categoryIds === '') {
        return selectedFromSearchFacet;
    }
    const flat = getFlatCategoryFacetsForCategoryIdResolution(rawFacets);
    const segments = categoryIds
        .toString()
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    const appearsInUrl = segments.some((seg) => flatFacetOptionIdMatches(seg, optionId));
    if (!appearsInUrl) {
        return selectedFromSearchFacet;
    }
    const resolution = resolveFlatFacetForCategoryId(flat, String(optionId).trim());
    return resolution?.id === targetFacetId;
};

type SearchFacetOptionForUrlSelection = {
    id?: string | number;
    name?: string;
    seoURL?: string;
};

export const isFacetOptionCheckedForSemanticUrl = (
    queryParams: Record<string, unknown>,
    targetFacetId: string,
    option: SearchFacetOptionForUrlSelection,
    selectedFromSearchFacet: boolean
): boolean => {
    const semanticParamName = getBrowseDimensionKeyFromFacetType(targetFacetId);
    if (!semanticParamName) {
        return selectedFromSearchFacet;
    }
    const rawParamValue = queryParams[semanticParamName];
    if (rawParamValue === undefined || rawParamValue === null || rawParamValue === '') {
        return selectedFromSearchFacet;
    }
    const normalizedParamValues = String(rawParamValue)
        .split(/[~,]/)
        .map((value) => value.trim())
        .filter(Boolean);
    if (normalizedParamValues.length === 0) {
        return selectedFromSearchFacet;
    }
    const normalizedOptionId = String(option.id ?? '').trim();
    const normalizedOptionSlug = urlSafeTitle(option.name ?? '');
    const normalizedOptionSeoSlug = String(option.seoURL ?? '')
        .trim()
        .toLowerCase();
    return normalizedParamValues.some(
        (paramValue) =>
            paramValue === normalizedOptionId ||
            paramValue === normalizedOptionSlug ||
            paramValue.toLowerCase() === normalizedOptionSeoSlug
    );
};

/**
 * Drops options whose id also appears on a higher-precedence flat facet (same order as `category_id` resolution),
 * so e.g. a style/period value like "Modern" is not listed again under Creator when aggregates reuse the same id.
 */
export const filterFlatFacetOptionsOwnedByPrecedence = <T extends { id?: string | number; name?: string }>(
    options: T[],
    rawFacets: FlatFacetForCategoryResolution[],
    displayFacetId: string
): T[] => {
    const precedence = FLAT_FACET_PRECEDENCE_FOR_SHARED_CATEGORY_ID;
    const myIndex = precedence.indexOf(displayFacetId);
    if (myIndex <= 0) {
        return options;
    }
    const blockedIds = new Set<string>();
    for (let i = 0; i < myIndex; i++) {
        const facet = rawFacets.find((f) => f.id === precedence[i]);
        facet?.options?.forEach((o) => blockedIds.add(String(o.id)));
    }
    let result = options.filter((o) => !blockedIds.has(String(o.id)));

    // Style values (e.g. "Modern") sometimes appear under creator with a different aggregate id; drop same label.
    if (displayFacetId === FacetTypes.CREATOR_ID) {
        const styleFacet = rawFacets.find((f) => f.id === FacetTypes.STYLE_PERIOD_ID);
        const styleLabels = new Set(
            styleFacet?.options?.map((o) => String(o.name).trim().toLowerCase()).filter((label) => label.length > 0) ??
                []
        );
        if (styleLabels.size > 0) {
            result = result.filter(
                (o) =>
                    !styleLabels.has(
                        String(o.name ?? '')
                            .trim()
                            .toLowerCase()
                    )
            );
        }
    }

    return result;
};

/**
 * buildSearchQuery - constructs a search-party query object from URL
 * parameters, handling status translation, range and distance facets,
 * SEO slug extraction, and target indexes (sold vs upcoming)
 * @param BuildSearchQueryParams options
 * @return SearchQuery
 */
export const buildSearchQuery = ({
    catalogId,
    defaultSortOrder = DEFAULT_SORT_ORDER,
    pathname = '/search',
    queryParams = {},
    browseFacetOptionIds,
    searchPageSize = 200,
    similarItems = false,
}: BuildSearchQueryParams): SearchQuery => {
    if (pathname?.includes('price-guide')) {
        const browse = parseBrowseCategoryPath(pathname);
        if (browse?.base === '/price-guide') {
            queryParams.keyword = decodeURIComponent(browse.categorySlug).replace(/-/g, ' ');
        } else {
            queryParams.keyword = getCategoryNameFromPathname(pathname);
        }
    }
    const similarItemsPage = isSimilarItemsPage(pathname);
    const status = translateStatus(queryParams, pathname);
    const isArchivedSearch = queryParams.status === 'archive' || (pathname && pathname.includes('/price-guide/'));
    const searchQuery: SearchQuery = {
        analyticsTags: ['web'],
        buyNow: queryParams.buyNow,
        buyNowDate: queryParams.buyNowDate,
        campaignId: queryParams.campaign_id,
        categories: [],
        distance: {},
        domesticFlatShipping: queryParams.domesticFlatShipping,
        options: { status },
        page: queryParams.page || 1,
        pageSize: searchPageSize,
        publishDate: {},
        ranges: {},
        saleDate: {},
        searchTerm: queryParams.keyword ? queryParams.keyword : '',
        ...(similarItems && { similarItems: true }),
        citySlug: '',
        region: '',
        sort: queryParams.sort || defaultSortOrder,
    };

    // SEO search pages use the slug to populate the search term
    if (getIsSEOSearchPage(pathname) || getIsCollectionsPage(pathname)) {
        const pathSegments = pathname.split('/').filter((segment) => segment !== '');

        searchQuery.searchTerm = pathSegments.pop() ?? '';
    } else if (getIsSEOSearchPageWithLocation(pathname)) {
        // SEO search pages with location use the slug to populate the search term
        const pathSegments = pathname.split('/').filter((segment) => segment !== '');
        const segments = {
            citySlug: pathSegments?.[2] ?? '',
            keyword: pathSegments?.[3] ?? '',
            region: pathSegments?.[1] ?? '',
        };

        searchQuery.searchTerm = segments.keyword;
        searchQuery.citySlug = segments.citySlug;
        searchQuery.region = segments.region;
    }

    if (queryParams.state && !searchQuery.region) {
        searchQuery.region = queryParams.state;
    }

    if (similarItemsPage) {
        searchQuery.analyticsTags.push('similar-items');
    }

    if (catalogId) {
        searchQuery.catalogId = catalogId;
    }

    const categoryIds = queryParams.category_id;
    if (categoryIds && searchQuery?.categories) {
        searchQuery.categories = categoryIds.toString().split(',');
    }

    Object.keys(queryParams).forEach((key) => {
        if (
            key === paramTypes.PAGE ||
            key === paramTypes.PAGE_SIZE_LEGACY_SUPPORT ||
            key === paramTypes.PAGE_SIZE ||
            key === paramTypes.CATEGORY_ID ||
            key === paramTypes.KEYWORD ||
            key === paramTypes.SORT ||
            key === paramTypes.STATUS ||
            key === paramTypes.CAMPAIGN_ID ||
            key === paramTypes.BUY_NOW ||
            key === paramTypes.BUY_NOW_DATE
        ) {
            return;
        }

        const convertedKey = getFacetFromSearchQueryParameter(key);
        const isRangeFacet =
            convertedKey === FacetTypes.ESTIMATED_PRICE_ID ||
            convertedKey === FacetTypes.CURRENT_BID_ID ||
            convertedKey === FacetTypes.HAMMER_PRICE_ID ||
            convertedKey === FacetTypes.RESERVE;
        const isDistanceFacet = convertedKey === FacetTypes.DISTANCE_ID;
        if (isRangeFacet && searchQuery?.ranges) {
            const minMaxMatches = queryParams[key].match(/-?\d+/g);
            if (!searchQuery?.ranges[convertedKey]) {
                searchQuery.ranges[convertedKey] = {};
            }
            const minValue = Number(minMaxMatches[0]);
            const maxValue = Number(minMaxMatches[1]);
            searchQuery.ranges[convertedKey].min = minValue === -1 ? undefined : minValue;
            searchQuery.ranges[convertedKey].max = maxValue === -1 ? undefined : maxValue;
        } else if (isDistanceFacet && !isArchivedSearch && queryParams[key] && queryParams[key].split) {
            const distanceOptions = queryParams[key].split(':');
            searchQuery.distance.country = distanceOptions[0];
            searchQuery.distance.zipCode = distanceOptions[1];
            searchQuery.distance.space = Number(distanceOptions[2]);
            searchQuery.distance.measurement = distanceOptions[3];
        } else if (key === paramTypes.PUBLISH_DATE_MIN) {
            searchQuery.publishDate.min = Number(queryParams[key]);
            searchQuery.publishDate.minSet = true;
        } else if (key === paramTypes.PUBLISH_DATE_MAX) {
            searchQuery.publishDate.max = Number(queryParams[key]);
            searchQuery.publishDate.maxSet = true;
        } else if (key === paramTypes.SALE_DATE_MIN) {
            searchQuery.saleDate.min = Number(queryParams[key]);
            searchQuery.saleDate.minSet = true;
        } else if (key === paramTypes.SALE_DATE_MAX) {
            searchQuery.saleDate.max = Number(queryParams[key]);
            searchQuery.saleDate.maxSet = true;
        } else if (key === paramTypes.FILTER_TERM) {
            searchQuery.filterTerm = queryParams[key].split(',').join(' AND ');
        } else if (queryParams[key] && queryParams[key].split) {
            const delimiter = getQueryParamValueDelimiter(key);
            const optionValues =
                delimiter === '~'
                    ? queryParams[key]
                          .split(/[~,]/)
                          .map((value) => value.trim())
                          .filter(Boolean)
                    : queryParams[key]
                          .split(delimiter)
                          .map((value) => value.trim())
                          .filter(Boolean);
            searchQuery.options[convertedKey] = optionValues;
        } else if (key === paramTypes.SHIPPING) {
            searchQuery.options[key] = [queryParams.shipping];
        }
    });

    if (browseFacetOptionIds) {
        Object.entries(browseFacetOptionIds).forEach(([facetType, ids]) => {
            if (ids?.length) {
                searchQuery.options[facetType] = [...ids];
            }
        });
    }

    return searchQuery;
};

export const isBuyNowQuery = (searchQuery: { options: any; sort: SearchSortAndDirection | undefined }) => {
    return Boolean(
        searchQuery.sort !== SearchSortAndDirection.TimeMostRecent &&
        searchQuery.options &&
        searchQuery.options['sale_type'] &&
        searchQuery.options['sale_type'].includes('BuyNow')
    );
};
